// List.vue 与 Search.vue 共用的列表页筛选逻辑：
// 路由 query <-> 筛选状态解析、where 参数构建、筛选项填充、已选条件管理与抽屉开关
// （layer 接口名由调用方传入 loadFilterOptions，故此处不引入 types）
import api from "@/utils/api";

// ===== 筛选默认展开配置（调整默认展开行为只改这里） =====
// 默认展开的筛选分组: 出现在数组中的分组默认展开, 未列出的默认收起
const DEFAULT_EXPAND_GROUPS = [
  "category",
  "genders",
  "colors",
  "sizes",
  "series",
  "brand",
  "clothingLength",
  "sleeveLength",
  "measurements",
];
// 分类树节点默认展开层数: 0=全部收起(仅分类组面板展开), 1=展开第一层分组, 2=展开两层
const CATEGORY_EXPAND_LEVEL = 0;
// 分组默认是否收起
const isGroupCollapse = (key) => DEFAULT_EXPAND_GROUPS.indexOf(key) === -1;

export default {
  data() {
    return {
      // 筛选过滤条件（默认展开/收起见顶部 DEFAULT_EXPAND_GROUPS 配置）
      filterOpts: {
        category: {
          items: [],
          checked: [],
          collapse: isGroupCollapse("category"),
        },
        genders: {
          items: [],
          checked: [],
          collapse: isGroupCollapse("genders"),
        },
        colors: { items: [], checked: [], collapse: isGroupCollapse("colors") },
        sizes: { items: [], checked: [], collapse: isGroupCollapse("sizes") },
        series: { items: [], checked: [], collapse: isGroupCollapse("series") },
        brand: { items: [], checked: [], collapse: isGroupCollapse("brand") },
        clothingLength: {
          items: [],
          checked: [],
          collapse: isGroupCollapse("clothingLength"),
        },
        sleeveLength: {
          items: [],
          checked: [],
          collapse: isGroupCollapse("sleeveLength"),
        },
        measurements: {
          items: [],
          checked: [],
          collapse: isGroupCollapse("measurements"),
        },
      },
      // 排序默认值: 「默认」选项, 传后端转空(buildWhereArgs)
      defaultSort: "default",
      sort: "",
      total: 0, // 总记录条数
      limit: 24, // 每页条数
      currPage: 1, // 当前页码
      filterDrawerVisible: false, // 移动端筛选抽屉是否显示
      showSelectedFilterItem: false, // 是否显示已选中的筛选过滤条件
    };
  },
  filters: {
    // 已选筛选 chip 的名称回显（树形/扁平结构通用）
    findFilterItem(ref, filterOptions) {
      if (!ref || !filterOptions?.items?.length) return ref;
      const findInList = (list) => {
        for (const cur of list) {
          if (typeof cur === "object") {
            if (cur?.id == ref || cur?.name == ref || cur?.value == ref)
              return cur;
            if (cur.children && cur.children.length) {
              const found = findInList(cur.children);
              if (found) return found;
            }
          } else if (cur == ref) return cur;
        }
        return null;
      };
      const item = findInList(filterOptions.items);
      return typeof item == "object" ? item.name || item.value : item;
    },
  },
  methods: {
    // 解析地址栏筛选/排序/分页参数到 filterOpts.checked / sort / limit / currPage
    parseRouteQuery(query) {
      let {
        genders,
        sizes,
        colors,
        sort,
        limit,
        page,
        category,
        series,
        brand,
        clothingLength,
        sleeveLength,
        measurements,
      } = query;
      category
        ? (this.filterOpts.category.checked = category.toString().split(","))
        : (this.filterOpts.category.checked = []);
      genders
        ? (this.filterOpts.genders.checked = genders.toString().split(","))
        : (this.filterOpts.genders.checked = []);
      sizes
        ? (this.filterOpts.sizes.checked = sizes.toString().split(","))
        : (this.filterOpts.sizes.checked = []);
      colors
        ? (this.filterOpts.colors.checked = colors.toString().split(","))
        : (this.filterOpts.colors.checked = []);
      series
        ? (this.filterOpts.series.checked = series.toString().split(","))
        : (this.filterOpts.series.checked = []);
      brand
        ? (this.filterOpts.brand.checked = brand.toString().split(","))
        : (this.filterOpts.brand.checked = []);
      clothingLength
        ? (this.filterOpts.clothingLength.checked = clothingLength
            .toString()
            .split(","))
        : (this.filterOpts.clothingLength.checked = []);
      sleeveLength
        ? (this.filterOpts.sleeveLength.checked = sleeveLength
            .toString()
            .split(","))
        : (this.filterOpts.sleeveLength.checked = []);
      measurements
        ? (this.filterOpts.measurements.checked = measurements
            .toString()
            .split(","))
        : (this.filterOpts.measurements.checked = []);
      // 'default' 为「默认」选项, 合法值回显到输入框, 传后端时转空(buildWhereArgs)
      sort &&
      ["default", "new", "sales", "price:desc", "price:asc"].indexOf(sort) != -1
        ? (this.sort = sort)
        : (this.sort = this.defaultSort);
      limit && [24, 48, 96].indexOf(parseInt(limit)) != -1
        ? (this.limit = parseInt(limit))
        : (this.limit = 24);
      if (page && !isNaN(page)) {
        page = parseInt(page, 10);
        page = page <= 0 ? 1 : page;
        this.currPage = page;
      } else {
        this.currPage = 1;
      }
      this.toggleFilterSelected();
    },
    // 根据 filterOpts 构建 where 查询参数（含排序/分页）
    buildWhereArgs() {
      let newArgs = {
        where: {
          category: "",
          gender: "",
          size: "",
          color: "",
          series: "",
          brand: "",
          clothingLength: "",
          sleeveLength: "",
          measurements: "",
        },
        // 「默认」仅是 UI 选项, 后端默认排序用空串
        sort: this.sort === "default" ? "" : this.sort,
        limit: this.limit,
        currPage: this.currPage,
      };
      let {
        category,
        genders,
        sizes,
        colors,
        series,
        brand,
        clothingLength,
        sleeveLength,
        measurements,
      } = this.filterOpts;
      if (
        category?.checked?.length ||
        genders?.checked?.length ||
        sizes?.checked?.length ||
        colors?.checked?.length ||
        series?.checked?.length ||
        brand?.checked?.length ||
        clothingLength?.checked?.length ||
        sleeveLength?.checked?.length ||
        measurements?.checked?.length
      ) {
        category?.checked?.length &&
          (newArgs.where.category = category.checked.toString());
        genders?.checked?.length &&
          (newArgs.where.gender = genders.checked.toString());
        sizes?.checked?.length &&
          (newArgs.where.size = sizes.checked.toString());
        colors?.checked?.length &&
          (newArgs.where.color = colors.checked.toString());
        series?.checked?.length &&
          (newArgs.where.series = series.checked.toString());
        brand?.checked?.length &&
          (newArgs.where.brand = brand.checked.toString());
        clothingLength?.checked?.length &&
          (newArgs.where.clothingLength = clothingLength.checked.toString());
        sleeveLength?.checked?.length &&
          (newArgs.where.sleeveLength = sleeveLength.checked.toString());
        measurements?.checked?.length &&
          (newArgs.where.measurements = measurements.checked.toString());
      }
      return newArgs;
    },
    // 填充筛选项：分类树 _collapse 必须在赋值给响应式状态"之前"初始化（赋值后 Vue 深度观测，后补属性不响应）
    applyFilters(_filters) {
      const getFilter = (code) =>
        _filters.find((i) => i.code === code)?.options;
      // 先收集旧分类树各分组节点的展开状态: 选筛选项/翻页等触发刷新后按 id 回填, 避免已展开的分类被重置收起
      const oldCollapseMap = {};
      const collectCollapse = (nodes) =>
        (nodes || []).forEach((n) => {
          if (n.children && n.children.length) {
            if (n._collapse !== undefined) oldCollapseMap[n.id] = n._collapse;
            collectCollapse(n.children);
          }
        });
      collectCollapse(this.filterOpts.category.items);
      const initCollapse = (nodes, level) =>
        (nodes || []).forEach((n) => {
          if (n.children && n.children.length) {
            // 旧树有记录则沿用用户展开状态; 否则按 CATEGORY_EXPAND_LEVEL 决定默认展开层数(level 从 0 计)
            n._collapse =
              oldCollapseMap[n.id] !== undefined
                ? oldCollapseMap[n.id]
                : level >= CATEGORY_EXPAND_LEVEL;
            initCollapse(n.children, level + 1);
          }
        });
      const categoryItems = getFilter("category") || [];
      initCollapse(categoryItems, 0);
      this.filterOpts.category.items = categoryItems;
      this.filterOpts.genders.items = getFilter("gender") || [];
      this.filterOpts.colors.items = getFilter("color") || [];
      this.filterOpts.sizes.items = getFilter("size") || [];
      this.filterOpts.series.items = getFilter("series") || [];
      this.filterOpts.brand.items = getFilter("brand") || [];
      this.filterOpts.clothingLength.items = getFilter("clothingLength") || [];
      this.filterOpts.sleeveLength.items = getFilter("sleeveLength") || [];
      this.filterOpts.measurements.items = getFilter("measurements") || [];
    },
    // 拉取筛选项
    loadFilterOptions(newArgs, layerApi) {
      api[layerApi](newArgs)
        .then((res) => {
          if (res?.code == 1) {
            this.applyFilters(res.data?.filters || []);
          } else {
            console.warn(res);
          }
        })
        .catch((err) => {
          console.warn(err);
        });
    },
    // 将所有分组恢复为 DEFAULT_EXPAND_GROUPS 配置的默认展开/收起状态(切换分类等场景用)
    resetFilterCollapse() {
      Object.keys(this.filterOpts).forEach(
        (k) => (this.filterOpts[k].collapse = isGroupCollapse(k)),
      );
    },
    // 判断已选中的过滤条件是否需要展示
    toggleFilterSelected() {
      let filterOpts = this.filterOpts;
      if (
        filterOpts?.category?.checked?.length ||
        filterOpts?.genders?.checked?.length ||
        filterOpts?.sizes?.checked?.length ||
        filterOpts?.colors?.checked?.length ||
        filterOpts?.series?.checked?.length ||
        filterOpts?.brand?.checked?.length ||
        filterOpts?.clothingLength?.checked?.length ||
        filterOpts?.sleeveLength?.checked?.length ||
        filterOpts?.measurements?.checked?.length
      ) {
        this.showSelectedFilterItem = true;
      } else {
        this.showSelectedFilterItem = false;
      }
    },
    // 打开移动端筛选抽屉
    openFilterDrawer() {
      this.filterDrawerVisible = true;
    },
    // 删除已选的过滤条件
    removeFilterItem(opt, ref) {
      let query = Object.assign({}, this.$route.query);
      let optCache = query[opt];
      if (optCache) {
        optCache = optCache.split(",");
        let idx = optCache.findIndex((i) => i == ref);
        optCache.splice(idx, 1);
        if (optCache.length) {
          query = Object.assign({}, query, { [opt]: optCache.toString() });
        } else {
          delete query[opt];
        }
        // 移除筛选条件后回到第 1 页
        delete query.page;
        this.$router.push({
          path: this.$route.path,
          query: query,
        });
      } else {
        console.warn(
          `Option: ${opt}, Ref: ${ref}, Query: ${JSON.stringify(query)}`,
        );
      }
    },
    // 清除所有过滤条件
    clearAll() {
      let newQuery = Object.assign({}, this.$route.query);
      delete newQuery.category;
      delete newQuery.genders;
      delete newQuery.sizes;
      delete newQuery.colors;
      delete newQuery.series;
      delete newQuery.brand;
      delete newQuery.clothingLength;
      delete newQuery.sleeveLength;
      delete newQuery.measurements;
      // 清除筛选条件后回到第 1 页
      delete newQuery.page;
      this.$router.push({
        path: this.$route.path,
        query: newQuery,
      });
    },
    // 更改显示数量、排序
    optsChange(opt) {
      if (["limit", "sort"].indexOf(opt) != -1) {
        let newQuery = Object.assign({}, this.$route.query);
        if (opt === "sort" && this.sort === "default") {
          // 'default' 为「默认」排序: 仅是 UI 状态, 不写入 url(传后端由 buildWhereArgs 转空)
          delete newQuery.sort;
        } else {
          newQuery[opt] = this[opt];
        }
        this.$router.push({
          path: this.$route.path,
          query: newQuery,
        });
      }
    },
    // 已选中的过滤条件回显（树形/扁平结构通用，series 的 checked 存的是 value）
    getCheckedList(option) {
      const { checked, items } = this.filterOpts[option];
      const isItemValid = (k) => !!this.findItemInTree(k, items);
      const ret = Array.isArray(checked)
        ? checked?.length
          ? checked.filter(isItemValid)
          : []
        : isItemValid(checked)
        ? [checked]
        : [];
      return ret;
    },
    // 在列表或树形结构中查找匹配 id/name/value 的项
    findItemInTree(ref, list) {
      if (!Array.isArray(list)) return null;
      for (const item of list) {
        if (typeof item === "object") {
          if (item?.id == ref || item?.name == ref || item?.value == ref)
            return item;
          if (item.children && item.children.length) {
            const found = this.findItemInTree(ref, item.children);
            if (found) return found;
          }
        } else if (item == ref) return item;
      }
      return null;
    },
  },
};
